import { type I18n, LocaleProvider, useLocale } from '@iris/i18n/react';
import { AuthProvider } from '@iris/ui/auth/auth-provider';
import { Toaster } from '@iris/ui/components/ui/sonner';
import { UiProvider } from '@iris/ui/components/ui-provider';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from 'react-router-dom';

import { apiClient, authBridge } from './api';
import { idbStorage } from './idb-storage';
import { clearSeed } from './qr/qr-seed';
import { PERSIST_MAX_AGE_MS, PERSISTED_QUERY_ROOTS, queryClient } from './query';
import { router } from './router';

const persister = createAsyncStoragePersister({ storage: idbStorage, key: 'iris-query-cache' });

/** Signed out or session lost: nothing of this account stays on the device (shared phones). */
function clearAccountData() {
  queryClient.clear();
  void persister.removeClient();
  void clearSeed(idbStorage); // a logged-out phone can no longer draw this customer's QR (T3 §3.5)
}

function Providers() {
  const { dir } = useLocale();
  return (
    <UiProvider dir={dir}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: PERSIST_MAX_AGE_MS,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) =>
              query.state.status === 'success' && PERSISTED_QUERY_ROOTS.includes(String(query.queryKey[0])),
          },
        }}
      >
        <AuthProvider app="CUSTOMER" client={apiClient} bridge={authBridge} onSignedOut={clearAccountData}>
          <RouterProvider router={router} />
        </AuthProvider>
      </PersistQueryClientProvider>
      <Toaster position="top-center" dir={dir} />
    </UiProvider>
  );
}

export function App({ i18n }: { i18n: I18n }) {
  return (
    <LocaleProvider i18n={i18n}>
      <Providers />
    </LocaleProvider>
  );
}
